import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { vec3 } from "gl-matrix";

import { type RootState } from "app";

import { Marker, MarkerCollection, MarkerCollectionConfig, MarkerCreationFlowConfig, MarkerData } from "./types";

const initialState = {
    collections: {} as Record<string, MarkerCollection>,
    // Stored as structured objects to avoid ambiguity if IDs contain colons (which would break string splitting).
    hoveredMarker: null as { collectionId: string; markerId: string } | null,
    // Serves also as the editing marker id. Stored as object to match hoveredMarker pattern.
    //
    // Selection/Deselection UX:
    // - Markers are selected when clicked (via pointer interaction in the canvas layer)
    // - After successful drag completion: marker REMAINS selected (confirmEditLocation keeps selectedMarker)
    // - After drag cancellation (Escape): marker REMAINS selected (cancelEditLocation keeps selectedMarker)
    // - Users can also deselect by:
    //   1. Clicking another marker (selection moves to the clicked marker)
    //   2. Clicking on background/empty space
    //   3. Clicking on a 3D object
    //   4. Pressing Escape key when a marker is selected but not being edited
    //   5. Calling MarkerCollection.deselectMarker() programmatically
    selectedMarker: null as { collectionId: string; markerId: string } | null,
    // Track whether the selected marker is ready to be dragged (true after first click completes)
    canDragSelectedMarker: false,
    // Track whether a pointer is currently down on any marker
    isPointerDownOnMarker: false,
    isEditingMarkerLocation: false,
    editPreviewPosition: null as vec3 | null,
    editOriginalPosition: null as vec3 | null,
    isCreatingMarker: false,
    creationCollectionId: null as string | null,
    creationPreviewPosition: null as vec3 | null,
    // World-space surface normal at the current placement/edit preview position.
    // Drives the surface-aligned placement highlight disc (showPlacementHighlight).
    // Only one placement interaction (creation or edit) is active at a time, so a
    // single field serves both flows.
    placementNormal: null as [number, number, number] | null,
};

export const markersSlice = createSlice({
    name: "markers",
    initialState,
    reducers: {
        // Collection management
        createCollection: (
            state,
            action: PayloadAction<{
                id: string;
                config: MarkerCollectionConfig;
                markerCreationConfig: MarkerCreationFlowConfig;
            }>,
        ) => {
            if (state.collections[action.payload.id]) {
                return;
            }

            state.collections[action.payload.id] = {
                id: action.payload.id,
                markers: {},
                config: action.payload.config,
                markerCreationConfig: action.payload.markerCreationConfig,
                visible: true,
            };
        },
        addCollection: (state, action: PayloadAction<MarkerCollection>) => {
            state.collections[action.payload.id] = action.payload;
        },
        removeCollection: (state, action: PayloadAction<string>) => {
            delete state.collections[action.payload];
        },
        setCollectionVisibility: (state, action: PayloadAction<{ id: string; visible: boolean }>) => {
            const collection = state.collections[action.payload.id];
            if (collection) {
                collection.visible = action.payload.visible;
            }
        },
        clearCollection: (state, action: PayloadAction<string>) => {
            const collection = state.collections[action.payload];
            if (collection) {
                collection.markers = {};
            }
        },
        updateCollectionConfig: (
            state,
            action: PayloadAction<{ id: string; config: Partial<MarkerCollectionConfig> }>,
        ) => {
            const collection = state.collections[action.payload.id];
            if (!collection) {
                return;
            }

            collection.config = {
                ...collection.config,
                ...action.payload.config,
            };
        },
        // Marker management
        addMarker: (state, action: PayloadAction<{ collectionId: string; marker: Marker }>) => {
            const collection = state.collections[action.payload.collectionId];
            if (collection) {
                const marker = action.payload.marker;
                const position: vec3 = marker.position;
                collection.markers[marker.id] = {
                    ...marker,
                    position,
                    collectionId: action.payload.collectionId,
                };
            }
        },
        removeMarker: (state, action: PayloadAction<{ collectionId: string; markerId: string }>) => {
            const collection = state.collections[action.payload.collectionId];
            if (collection) {
                delete collection.markers[action.payload.markerId];
            }
        },
        updateMarker: (
            state,
            action: PayloadAction<{ collectionId: string; markerId: string; updates: Partial<MarkerData> }>,
        ) => {
            const collection = state.collections[action.payload.collectionId];
            if (!collection) {
                return;
            }

            const marker = collection.markers[action.payload.markerId];
            if (!marker) {
                return;
            }

            collection.markers[action.payload.markerId] = {
                ...marker,
                ...action.payload.updates,
            };
        },
        // Interaction state
        setHoveredMarker: (state, action: PayloadAction<{ collectionId: string; markerId: string } | null>) => {
            state.hoveredMarker = action.payload;
        },
        setSelectedMarker: (state, action: PayloadAction<{ collectionId: string; markerId: string } | null>) => {
            // When selecting a different marker, reset canDragSelectedMarker
            if (
                action.payload &&
                (state.selectedMarker?.collectionId !== action.payload.collectionId ||
                    state.selectedMarker?.markerId !== action.payload.markerId)
            ) {
                state.canDragSelectedMarker = false;
            }
            state.selectedMarker = action.payload;
        },
        deselectMarker: (state) => {
            state.selectedMarker = null;
            state.canDragSelectedMarker = false;
        },
        enableDragSelectedMarker: (state) => {
            // Enable dragging for currently selected marker (called after first click completes)
            if (state.selectedMarker) {
                state.canDragSelectedMarker = true;
            }
        },
        setPointerDownOnMarker: (state, action: PayloadAction<boolean>) => {
            state.isPointerDownOnMarker = action.payload;
        },
        // Editing state
        startEditLocation: (state, action: PayloadAction<{ collectionId: string; markerId: string }>) => {
            const collection = state.collections[action.payload.collectionId];
            if (!collection) {
                // Collection doesn't exist - silently ignore (marker may have been removed)
                return;
            }

            const marker = collection.markers[action.payload.markerId];
            if (!marker) {
                // Marker doesn't exist - silently ignore (marker may have been removed between pointerdown and drag start)
                return;
            }

            // Store structured object to avoid ambiguity if IDs contain colons
            state.selectedMarker = {
                collectionId: action.payload.collectionId,
                markerId: action.payload.markerId,
            };
            state.isEditingMarkerLocation = true;
            state.editPreviewPosition = null;
            state.editOriginalPosition = marker.position;
        },
        confirmEditLocation: (state) => {
            state.isEditingMarkerLocation = false;
            state.editPreviewPosition = null;
            state.editOriginalPosition = null;
            state.canDragSelectedMarker = true;
            state.placementNormal = null;
        },
        cancelEditLocation: (state) => {
            state.isEditingMarkerLocation = false;
            state.editPreviewPosition = null;
            state.editOriginalPosition = null;
            state.canDragSelectedMarker = true;
            state.placementNormal = null;
        },
        updateEditPreview: (state, action: PayloadAction<[number, number, number]>) => {
            state.editPreviewPosition = action.payload;
        },
        // Creation state
        startCreateMarker: (state, action: PayloadAction<{ collectionId: string }>) => {
            state.creationCollectionId = action.payload.collectionId;
            state.isCreatingMarker = true;
            state.creationPreviewPosition = null;
        },
        stopCreateMarker: (state) => {
            state.isCreatingMarker = false;
            state.creationCollectionId = null;
            state.creationPreviewPosition = null;
            state.placementNormal = null;
        },
        updateCreationPreview: (state, action: PayloadAction<[number, number, number] | null>) => {
            state.creationPreviewPosition = action.payload;
            if (action.payload === null) {
                state.placementNormal = null;
            }
        },
        // Updated alongside creation/edit preview positions from the same pick result.
        // Null hides the placement highlight disc (e.g. pick miss or plane-fallback drag).
        updatePlacementNormal: (state, action: PayloadAction<[number, number, number] | null>) => {
            state.placementNormal = action.payload;
        },
        updateCreationConfig: (
            state,
            action: PayloadAction<{ id: string; config: Partial<MarkerCreationFlowConfig> }>,
        ) => {
            const collection = state.collections[action.payload.id];
            if (!collection) {
                return;
            }

            collection.markerCreationConfig = {
                ...collection.markerCreationConfig,
                ...action.payload.config,
            };
        },
        reset: (state) => {
            Object.assign(state, initialState);
        },
    },
});

// Actions and reducer
const { actions, reducer } = markersSlice;
export { actions as markersActions, reducer as markersReducer };

// Selectors
export const selectAllCollections = (state: RootState) => state.markers.collections;
export const selectCollection = (id: string) => (state: RootState) => state.markers.collections[id];
export const selectAllMarkers = (state: RootState) =>
    Object.values(state.markers.collections).flatMap((collection) => Object.values(collection.markers));
export const selectMarker =
    (markerId: string) =>
    (state: RootState): Marker | null => {
        for (const collection of Object.values(state.markers.collections)) {
            const marker = collection.markers[markerId];
            if (marker) {
                return marker;
            }
        }
        return null;
    };
export const selectHoveredMarker = (state: RootState) => state.markers.hoveredMarker;
export const selectSelectedMarker = (state: RootState) => state.markers.selectedMarker;
export const selectIsEditingMarkerLocation = (state: RootState) => state.markers.isEditingMarkerLocation;
export const selectIsPointerDownOnMarker = (state: RootState) => state.markers.isPointerDownOnMarker;
export const selectIsMarkerPointerInteractionActive = (state: RootState) =>
    state.markers.isEditingMarkerLocation || state.markers.isPointerDownOnMarker;
export const selectCanDragSelectedMarker = (state: RootState) => state.markers.canDragSelectedMarker;
export const selectEditPreviewPosition = (state: RootState) => state.markers.editPreviewPosition;
export const selectEditOriginalPosition = (state: RootState) => state.markers.editOriginalPosition;
export const selectIsCreatingMarker = (state: RootState) => state.markers.isCreatingMarker;
export const selectCreationCollectionId = (state: RootState) => state.markers.creationCollectionId;
export const selectCreationPreviewPosition = (state: RootState) => state.markers.creationPreviewPosition;
export const selectPlacementNormal = (state: RootState) => state.markers.placementNormal;
